// client/src/pages/Referral.tsx
import React, { useState, useEffect } from 'react';
import { Users, Send, CheckCircle, Gift, Copy, Facebook, Twitter, Share2, Loader2 } from 'lucide-react';

import type { 
  ReferralStats, 
  ReferralActivity, 
  TopReferrer, 
  ReferralProgress, 
  Reward 
} from '@/api/services/referralService';
import referralService from '@/api/services/referralService';
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { getErrorMessage } from "@/utils/errorUtils";




export default function ReferralProgram() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for all data
  const [referralLink, setReferralLink] = useState('');
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [activities, setActivities] = useState<ReferralActivity[]>([]);
  const [leaderboard, setLeaderboard] = useState<TopReferrer[]>([]);
  const [currentUserRank, setCurrentUserRank] = useState<{ rank: number; totalReferrals: number } | null>(null);
  const [progress, setProgress] = useState<ReferralProgress | null>(null);
  const [rewards, setRewards] = useState<Reward[]>([]);

  // Fetch all data on mount
  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const results = await Promise.allSettled([
        referralService.getReferralLink(),
        referralService.getStats(),
        referralService.getActivity({ limit: 10 }),
        referralService.getLeaderboard({ limit: 10 }),
        referralService.getProgress(),
        referralService.getRewards(),
      ]);

      const [linkRes, statsRes, activityRes, leaderboardRes, progressRes, rewardsRes] = results;
      let failedCount = 0;

      // Referral link
      if (linkRes.status === 'fulfilled') {
        const rawLink = linkRes.value.referralLink || '';
        // Always build the link from the CURRENT origin so the domain is correct
        // (e.g. konectx.net), regardless of any stale base URL stored in the DB.
        const codeMatch = rawLink.match(/[?&]ref=([^&]+)/);
        const code = codeMatch ? codeMatch[1] : linkRes.value.referralCode || '';
        setReferralLink(code ? `${location.origin}/register?ref=${code}` : rawLink);
      } else {
        failedCount++;
        console.error('Failed to fetch referral link:', linkRes.reason);
      }

      if (statsRes.status === 'fulfilled') setStats(statsRes.value);
      else { failedCount++; console.error('Stats failed:', statsRes.reason); }

      if (activityRes.status === 'fulfilled') setActivities(activityRes.value.activities || []);
      else { failedCount++; console.error('Activity failed:', activityRes.reason); }

      if (leaderboardRes.status === 'fulfilled') {
        setLeaderboard(leaderboardRes.value.leaderboard || []);
        setCurrentUserRank(leaderboardRes.value.currentUser || null);
      } else { failedCount++; console.error('Leaderboard failed:', leaderboardRes.reason); }

      if (progressRes.status === 'fulfilled') setProgress(progressRes.value);
      else { failedCount++; console.error('Progress failed:', progressRes.reason); }

      if (rewardsRes.status === 'fulfilled') setRewards(rewardsRes.value.rewards || []);
      else { failedCount++; console.error('Rewards failed:', rewardsRes.reason); }

      // Only show full-page error if ALL six failed (likely auth / network issue).
      // Partial failures degrade gracefully — user still sees what loaded.
      if (failedCount === 6) {
        setError(t('operator.referralsPage.errors.loadData', { defaultValue: 'Failed to load referral data' }));
      }
    } catch (err: unknown) {
      // Unexpected sync error (e.g. broken response shape). Each individual API
      // call is now handled via Promise.allSettled above, so this branch should
      // be rare.
      console.error('Unexpected error fetching referral data:', err);
      setError(getErrorMessage(err) || t('operator.referralsPage.errors.loadData', { defaultValue: 'Failed to load referral data' }));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (isSharing) return;
    setIsSharing(true);
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      // Fire-and-forget track call — copy itself already succeeded, so we
      // don't want a slow analytics request to delay the UI feedback.
      referralService.trackShare('link').catch((err) => {
        console.error('Failed to track copy share:', err);
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err: unknown) {
      console.error('Failed to copy:', err);
    } finally {
      setIsSharing(false);
    }
  };

  const handleShare = async (platform: 'facebook' | 'twitter' | 'other') => {
    if (isSharing) return;
    setIsSharing(true);
    try {
      // Open share window FIRST so the popup is tied to the user gesture.
      // Pop-up blockers reject window.open() that comes after an await.
      if (platform === 'facebook') {
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralLink)}`, '_blank');
      } else if (platform === 'twitter') {
        const text = t('operator.referralsPage.shareText', { defaultValue: 'Join me on this amazing platform!' });
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(referralLink)}`, '_blank');
      } else {
        // 'other' — fall back to navigator.share if available, otherwise just track
        if (typeof navigator !== 'undefined' && (navigator as unknown as { share: (data: { title: string; text: string; url: string }) => Promise<void> }).share) {
          try {
            await (navigator as unknown as { share: (data: { title: string; text: string; url: string }) => Promise<void> }).share({
              title: t('operator.referralsPage.shareTitle', { defaultValue: 'Join KonectX' }),
              text: t('operator.referralsPage.shareText', { defaultValue: 'Join me on this amazing platform!' }),
              url: referralLink,
            });
          } catch (shareErr: unknown) {
            // AbortError = user cancelled the share sheet; not an error worth logging
            if (shareErr instanceof Error && shareErr.name !== 'AbortError') {
              console.error('Native share failed:', shareErr);
            }
          }
        }
      }
      // Fire-and-forget tracking — the share itself already happened.
      referralService.trackShare(platform).catch((err) => {
        console.error('Failed to track share:', err);
      });
    } finally {
      setIsSharing(false);
    }
  };

  const getActivityIcon = (activity: ReferralActivity) => {
    if (activity.status === 'pending') return Send;
    if (activity.rewardType === 'achievement') return Gift;
    return CheckCircle;
  };

  // Returns a STATIC Tailwind class set per activity. Tailwind purges unused
  // classes at build time, so dynamic class names like `bg-${color}-100` never
  // make it into the final CSS bundle. We must list each variant explicitly.
  const getActivityClasses = (activity: ReferralActivity): { bg: string; icon: string } => {
    if (activity.status === 'pending') {
      return {
        bg: 'bg-yellow-100 dark:bg-yellow-900',
        icon: 'text-yellow-600 dark:text-yellow-400',
      };
    }
    if (activity.rewardType === 'achievement') {
      return {
        bg: 'bg-purple-100 dark:bg-purple-900',
        icon: 'text-purple-600 dark:text-purple-400',
      };
    }
    return {
      bg: 'bg-green-100 dark:bg-green-900',
      icon: 'text-green-600 dark:text-green-400',
    };
  };

  const formatTime = (dateString?: string | null) => {
    if (!dateString) return t('operator.referralsPage.time.na', { defaultValue: 'N/A' });
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return t('operator.referralsPage.time.na', { defaultValue: 'N/A' });
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 0) return t('operator.referralsPage.time.justNow', { defaultValue: 'Just now' });
    if (diffInHours < 1) return t('operator.referralsPage.time.lessThanHour', { defaultValue: 'Less than an hour ago' });
    if (diffInHours < 24) {
      return diffInHours === 1
        ? t('operator.referralsPage.time.hourAgo', { defaultValue: '1 hour ago' })
        : t('operator.referralsPage.time.hoursAgo', { defaultValue: '{{count}} hours ago', count: diffInHours });
    }

    const diffInDays = Math.floor(diffInHours / 24);
    return diffInDays === 1
      ? t('operator.referralsPage.time.dayAgo', { defaultValue: '1 day ago' })
      : t('operator.referralsPage.time.daysAgo', { defaultValue: '{{count}} days ago', count: diffInDays });
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchAllData}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('operator.referralsPage.retry', { defaultValue: 'Retry' })}
          </button>
        </div>
      </div>
    );
  }

  const statsCards = [
    { 
      label: t('operator.referralsPage.stats.totalReferrals', { defaultValue: 'Total Referrals' }), 
      value: stats?.totalReferrals || 0, 
      icon: Users, 
      color: 'text-gray-600 dark:text-gray-400' 
    },
    { 
      label: t('operator.referralsPage.stats.pending', { defaultValue: 'Pending' }), 
      value: stats?.pendingReferrals || 0, 
      icon: Send, 
      color: 'text-yellow-500' 
    },
    { 
      label: t('operator.referralsPage.stats.approved', { defaultValue: 'Approved' }), 
      value: stats?.approvedReferrals || 0, 
      icon: CheckCircle, 
      color: 'text-green-500' 
    },
    {
      label: t('operator.referralsPage.stats.totalEarnings', { defaultValue: 'Total Earnings (USD)' }),
      value: `$${(stats?.totalEarnings || 0).toLocaleString()}`,
      icon: Gift,
      color: 'text-purple-500'
    }
  ];

  return (
    <>
      <Helmet>
        <title>{t('operator.referralsPage.meta.title', { defaultValue: 'Referral Program | KonectX' })}</title>
        <meta
          name="description"
          content={t('operator.referralsPage.meta.description', { defaultValue: 'Invite friends to KonectX and earn rewards with the Referral Program. Share your referral code and grow the decentralized network.' })}
        />
        <meta
          name="keywords"
          content={t('operator.referralsPage.meta.keywords', { defaultValue: 'KonectX, referral program, invite friends, KXT token, rewards, decentralized network' })}
        />
        <meta property="og:title" content={t('operator.referralsPage.meta.ogTitle', { defaultValue: 'Referral Program - KonectX' })} />
        <meta
          property="og:description"
          content={t('operator.referralsPage.meta.description', { defaultValue: 'Invite friends to KonectX and earn rewards with the Referral Program. Share your referral code and grow the decentralized network.' })}
        />
      </Helmet>

   <div className="w-full p-7">
      {/* Title and Description */}
      <div className="text-center mb-6">
        <p className="text-gray-600 dark:text-gray-400 text-sm">
          {t('operator.referralsPage.subtitle', { defaultValue: 'Invite friends to join our platform and earn amazing rewards. The more friends you bring, the more rewards you unlock!' })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-6">
        {statsCards.map((stat, idx) => (
          <div key={idx} className="bg-white dark:bg-[#333436] rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 dark:text-white text-xl">{stat.label}</span>
              <stat.icon className={`w-5 h-5 ${stat.color}`} />
            </div>
            <div className="text-3xl font-bold text-gray-900 dark:text-white">{stat.value}</div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Referral Link Section */}
          <div className="bg-white dark:bg-[#333436] rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-black dark:text-white mb-4">{t('operator.referralsPage.yourReferralLink', { defaultValue: 'Your Referral Link' })}</h2>
            <div className="flex gap-2 sm:gap-3 mb-4">
              <input
                type="text"
                value={referralLink}
                readOnly
                className="flex-1 min-w-0 px-3 sm:px-4 py-3 bg-gray-50 dark:bg-[#2a2b2d] border border-gray-200 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300"
              />
              <button
                onClick={handleCopy}
                disabled={isSharing}
                className="shrink-0 px-4 sm:px-6 py-3 bg-[#2E5A88] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#244a73] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {copied ? (
                  <CheckCircle className="w-4 h-4 shrink-0" />
                ) : (
                  <Copy className="w-4 h-4 shrink-0" />
                )}
                <span className="whitespace-nowrap">
                  {copied
                    ? t('operator.referralsPage.copied', { defaultValue: 'Copied!' })
                    : t('operator.referralsPage.copy', { defaultValue: 'Copy' })}
                </span>
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <button
                onClick={() => handleShare('facebook')}
                disabled={isSharing}
                className="px-4 py-3 bg-[#1877F2] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#0d65d9] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Facebook className="w-4 h-4" />
                {t('operator.referralsPage.shareFacebook', { defaultValue: 'Share on Facebook' })}
              </button>
              <button
                onClick={() => handleShare('twitter')}
                disabled={isSharing}
                className="px-4 py-3 bg-black text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Twitter className="w-4 h-4" />
                {t('operator.referralsPage.shareTwitter', { defaultValue: 'Share on X' })}
              </button>
              <button
                onClick={() => handleShare('other')}
                disabled={isSharing}
                className="px-4 py-3 bg-[#2E5A88] text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#244a73] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Share2 className="w-4 h-4" />
                {t('operator.referralsPage.shareMore', { defaultValue: 'Share elsewhere' })}
              </button>
            </div>
          </div>

          {/* Progress to Next Reward */}
          {progress && (
            <div className="bg-white dark:bg-[#333436] rounded-xl p-6 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-black dark:text-white">{t('operator.referralsPage.progressToNextReward', { defaultValue: 'Progress to Next Reward' })}</h2>
                <span className="text-sm text-black dark:text-gray-400">
                  {t('operator.referralsPage.nextReward', { defaultValue: 'Next Reward' })}: {progress.nextMilestone?.reward || t('operator.referralsPage.maxTierAchieved', { defaultValue: 'Max Tier Achieved' })}
                </span>
              </div>
              <div className="mb-6">
                <div className="text-sm text-black dark:text-gray-400 mb-2">
                  {progress.totalReferrals}/{progress.nextMilestone?.referralsRequired || progress.milestones[progress.milestones.length - 1]?.referralsRequired} {t('operator.referralsPage.referrals', { defaultValue: 'Referrals' })}
                </div>
                <div className="h-3 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-black dark:bg-blue-600 transition-all duration-500" 
                    style={{ width: `${progress.progress}%` }}
                  ></div>
                </div>
              </div>
              {/* Milestone Markers */}
              <div className="flex justify-between items-start">
                {progress.milestones.map((milestone) => (
                  <div key={milestone.name} className="text-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 mx-auto ${
                      milestone.achieved 
                        ? 'bg-green-100 dark:bg-green-900' 
                        : 'bg-gray-100 dark:bg-gray-700'
                    }`}>
                      {milestone.achieved ? (
                        <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400" />
                      ) : (
                        <Gift className="w-6 h-6 text-gray-400" />
                      )}
                    </div>
                    <div className="text-xs font-bold text-black dark:text-white">{milestone.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{milestone.referralsRequired} {t('operator.referralsPage.referrals', { defaultValue: 'Referrals' })}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity */}
          <div className="bg-white dark:bg-[#333436] rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('operator.referralsPage.recentActivity', { defaultValue: 'Recent Activity' })}</h2>
            {activities.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-8">{t('operator.referralsPage.noActivity', { defaultValue: 'No referral activity yet' })}</p>
            ) : (
              <div className="space-y-3">
                {activities.map((activity) => {
                  const Icon = getActivityIcon(activity);
                  const classes = getActivityClasses(activity);

                  return (
                    <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-[#2a2b2d] rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${classes.bg}`}>
                          <Icon className={`w-5 h-5 ${classes.icon}`} />
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {activity.referredUser.name}{' '}
                            <span className="font-normal text-gray-600 dark:text-gray-400">
                              {t('operator.referralsPage.joinedUsingReferral', { defaultValue: 'joined using your referral' })}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {formatTime(activity.createdAt)}
                          </div>
                        </div>
                      </div>
                      <div className={`text-sm font-semibold ${
                        activity.status === 'approved' && activity.earningsAwarded > 0
                          ? 'text-green-600 dark:text-green-400'
                          : activity.status === 'pending'
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-purple-600 dark:text-purple-400'
                      }`}>
                        {activity.status === 'approved' && activity.earningsAwarded > 0
                          ? `+$${Number(activity.earningsAwarded).toLocaleString()} USD`
                          : activity.status === 'pending'
                            ? t('operator.referralsPage.stats.pending', { defaultValue: 'Pending' })
                            : activity.status === 'approved'
                              ? t('operator.referralsPage.stats.approved', { defaultValue: 'Approved' })
                              : activity.status.charAt(0).toUpperCase() + activity.status.slice(1)
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right Column - 1/3 width */}
        <div className="space-y-6">
          {/* Top Referrers */}
          <div className="bg-white dark:bg-[#333436] rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-black dark:text-white mb-4">{t('operator.referralsPage.topReferrers', { defaultValue: 'Top Referrers' })}</h2>
            {leaderboard.length === 0 ? (
              <p className="text-center text-gray-500 dark:text-gray-400 py-4">{t('operator.referralsPage.noLeaderboard', { defaultValue: 'No leaderboard data yet' })}</p>
            ) : (
              <div className="space-y-3">
                {leaderboard.map((referrer, index) => (
                  <div key={referrer.userId} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0
                          ? 'bg-yellow-500 text-white'         // Gold
                          : index === 1
                            ? 'bg-gray-400 text-white'         // Silver
                            : index === 2
                              ? 'bg-orange-500 text-white'     // Bronze
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                      }`}>
                        {index + 1}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{referrer.name}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{referrer.totalReferrals} {t('operator.referralsPage.referrals', { defaultValue: 'Referrals' })}</div>
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      ${Number(referrer.totalEarnings || 0).toLocaleString()} USD
                    </div>
                  </div>
                ))}
                
                {/* Show current user if they're NOT already in the visible leaderboard.
                    We check by userId rather than by rank vs. leaderboard.length,
                    because the user's rank can be within the top N (e.g. rank 5 of 7
                    shown) and still pass `rank > length` if length is smaller. */}
                {currentUserRank && !leaderboard.some((r) => (r as unknown as Record<string, unknown>).userId === (currentUserRank as unknown as Record<string, unknown>).userId) && currentUserRank.rank > leaderboard.length && (
                  <>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-3"></div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          {currentUserRank.rank}
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{t('operator.referralsPage.you', { defaultValue: 'You' })}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{currentUserRank.totalReferrals} {t('operator.referralsPage.referrals', { defaultValue: 'Referrals' })}</div>
                        </div>
                      </div>
                      <div className="text-sm font-semibold text-gray-900 dark:text-white">
                        ${Number(stats?.totalEarnings || 0).toLocaleString()} USD
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Available Rewards */}
          <div className="bg-white dark:bg-[#333436] rounded-xl p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('operator.referralsPage.availableRewards', { defaultValue: 'Available Rewards' })}</h2>
            <div className="space-y-4">
              {rewards.map((reward) => (
                <div 
                  key={reward.id} 
                  className={`pb-4 border-b border-gray-100 dark:border-gray-700 last:border-0 last:pb-0 ${
                    reward.available ? 'opacity-100' : 'opacity-60'
                  }`}
                >
                  <div className="flex items-start justify-between mb-1">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                      {reward.name}
                      {reward.available && (
                        <span className="text-xs bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-0.5 rounded">
                          {t('operator.referralsPage.available', { defaultValue: 'Available' })}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {reward.referralsRequired} {t('operator.referralsPage.referralsLower', { defaultValue: 'referrals' })}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{reward.description}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
}
